import { useState } from 'react';
import { Button, Dialog, useToast } from '@/components/ui';
import { isApiError, useArchiveConversation, type Conversation } from '@/lib/api';
import './DeleteConversationDialog.css';

export interface DeleteConversationDialogProps {
  conversation: Conversation;
  onClose: () => void;
  /** Depois de excluir, com o histórico já relido. */
  onDeleted?: () => void;
}

/**
 * A confirmação de "Excluir". Na API é o arquivamento: a conversa sai de todas
 * as listas e não abre mais. Uma recusa fica no diálogo, que continua aberto.
 */
export function DeleteConversationDialog({
  conversation,
  onClose,
  onDeleted,
}: DeleteConversationDialogProps): JSX.Element {
  const archive = useArchiveConversation();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);

  const confirm = async (): Promise<void> => {
    setError(null);
    try {
      await archive.mutateAsync(conversation.id);
      toast({ tone: 'success', title: 'Conversa excluída.' });
      onClose();
      onDeleted?.();
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Não foi possível excluir a conversa. Tente novamente.');
    }
  };

  return (
    <Dialog
      open
      onClose={() => !archive.isPending && onClose()}
      size="sm"
      title="Excluir conversa?"
      description={`"${conversation.title}" sai do seu histórico. Esta ação não pode ser desfeita.`}
      dismissible={!archive.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={archive.isPending}>
            Cancelar
          </Button>
          <Button variant="danger" loading={archive.isPending} onClick={() => void confirm()}>
            Excluir
          </Button>
        </>
      }
    >
      {error && (
        <p className="conversation-delete__error" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}
